/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AnalyticsDto,
  AnalyticsSummaryDto,
  ApiErrorDto,
  GetAnalyticsParams,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Growth over a range, per metric and per account (30 days by default).
 */
export const getAnalytics = (
  id: string,
  params?: GetAnalyticsParams,
  signal?: AbortSignal,
) => {
  return customInstance<AnalyticsDto>({
    url: `/api/v1/workspaces/${id}/analytics`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetAnalyticsQueryKey = (id: string, params?: GetAnalyticsParams) => {
  return [`/api/v1/workspaces/${id}/analytics`, ...(params ? [params] : [])] as const;
};

export const getGetAnalyticsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAnalytics>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetAnalyticsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalytics>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAnalyticsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAnalytics>>> = ({ signal }) =>
    getAnalytics(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAnalytics>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAnalyticsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAnalytics>>
>;
export type GetAnalyticsQueryError = ErrorType<ApiErrorDto>;

export function useGetAnalytics<
  TData = Awaited<ReturnType<typeof getAnalytics>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: undefined | GetAnalyticsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalytics>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalytics>>,
          TError,
          Awaited<ReturnType<typeof getAnalytics>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAnalytics<
  TData = Awaited<ReturnType<typeof getAnalytics>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetAnalyticsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalytics>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalytics>>,
          TError,
          Awaited<ReturnType<typeof getAnalytics>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAnalytics<
  TData = Awaited<ReturnType<typeof getAnalytics>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetAnalyticsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalytics>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Growth over a range, per metric and per account (30 days by default).
 */

export function useGetAnalytics<
  TData = Awaited<ReturnType<typeof getAnalytics>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: GetAnalyticsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalytics>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAnalyticsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary This week so far vs the same days last week: views, reels out, watch time.
 */
export const getAnalyticsSummary = (id: string, signal?: AbortSignal) => {
  return customInstance<AnalyticsSummaryDto>({
    url: `/api/v1/workspaces/${id}/analytics/summary`,
    method: 'GET',
    signal,
  });
};

export const getGetAnalyticsSummaryQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/analytics/summary`] as const;
};

export const getGetAnalyticsSummaryQueryOptions = <
  TData = Awaited<ReturnType<typeof getAnalyticsSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalyticsSummary>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAnalyticsSummaryQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAnalyticsSummary>>> = ({
    signal,
  }) => getAnalyticsSummary(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAnalyticsSummary>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAnalyticsSummaryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAnalyticsSummary>>
>;
export type GetAnalyticsSummaryQueryError = ErrorType<ApiErrorDto>;

export function useGetAnalyticsSummary<
  TData = Awaited<ReturnType<typeof getAnalyticsSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalyticsSummary>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalyticsSummary>>,
          TError,
          Awaited<ReturnType<typeof getAnalyticsSummary>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAnalyticsSummary<
  TData = Awaited<ReturnType<typeof getAnalyticsSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalyticsSummary>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalyticsSummary>>,
          TError,
          Awaited<ReturnType<typeof getAnalyticsSummary>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAnalyticsSummary<
  TData = Awaited<ReturnType<typeof getAnalyticsSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalyticsSummary>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary This week so far vs the same days last week: views, reels out, watch time.
 */

export function useGetAnalyticsSummary<
  TData = Awaited<ReturnType<typeof getAnalyticsSummary>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalyticsSummary>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAnalyticsSummaryQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
